import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useHistory, useParams } from "react-router-dom";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import Navbar from "../components/Navbar";
import { customerIdOf } from "../hooks/useAnalyticsUser";
import { forgetMyGroupBaskets } from "../hooks/useMyGroupBaskets";
import { fetchGroupJoinPreview, joinGroupBasket } from "../api/groupBasketApi";
import { trackGroupBasketJoin } from "../utils/analytics";
import { goToSignIn } from "../utils/signInReturn";
import {
  accountCreatedFromGroupLink,
  clearGroupBasketToken,
  groupBasketPath,
  groupJoinPath,
  rememberGroupBasketToken,
} from "../utils/groupBasketLink";
import { MODE } from "../utils/groupBasketView";
import GroupBasketCountdown from "../components/GroupBasket/GroupBasketCountdown";
import {
  Actions,
  Card,
  CardTitle,
  Eyebrow,
  FieldLabel,
  Hero,
  HeroInner,
  HeroLine,
  HeroTitle,
  Muted,
  Notice,
  Page,
  PrimaryButton,
  QuietButton,
  Shell,
  Stack,
  TextInput,
} from "../components/GroupBasket/groupBasketStyles";

const NOT_JOINABLE_TEXT = {
  BASKET_NOT_OPEN: "This basket is no longer taking items.",
  BASKET_FULL: "This basket is full.",
  MARKET_MISMATCH: "This basket is in another country. Open the link on that country's Lusuku website.",
};

const firstNameOf = (user) =>
  String(user?.result?.name || user?.name || "")
    .trim()
    .split(/\s+/)[0]
    .slice(0, 24);

/*
 * The page an Order together link opens (/g/:token). Anyone can see who
 * invited them and to which restaurant; joining needs an account, so a
 * signed-out visitor goes through sign in (or sign up, carrying the token)
 * and comes straight back here.
 */
const GroupBasketJoinPage = () => {
  const { token } = useParams();
  const history = useHistory();
  const user = useSelector((state) => state.user?.currentUser);
  const signedIn = Boolean(customerIdOf(user));
  const [preview, setPreview] = useState(null);
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState(null);
  const [receivedAt, setReceivedAt] = useState(0);
  const [label, setLabel] = useState(() => firstNameOf(user));
  const [joining, setJoining] = useState(false);
  const [joinError, setJoinError] = useState("");

  const load = useCallback(async () => {
    setStatus("loading");
    try {
      const answer = await fetchGroupJoinPreview(token);
      setPreview(answer);
      setReceivedAt(Date.now());
      setStatus(answer ? "ready" : "failed");
      if (answer?.alreadyMember && answer.basketId) {
        clearGroupBasketToken();
        history.replace(groupBasketPath(answer.basketId));
      }
    } catch (err) {
      setError(err);
      setStatus(err.code === "LINK_INVALID" ? "invalid" : "failed");
    }
  }, [token, history]);

  useEffect(() => {
    load();
  }, [load, signedIn]);

  // A visitor who may create an account here carries the link to sign up.
  useEffect(() => {
    if (!signedIn) rememberGroupBasketToken(token);
  }, [signedIn, token]);

  useEffect(() => {
    if (signedIn && !label) setLabel(firstNameOf(user));
    // Only when the shopper signs in on this page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signedIn]);

  const join = async () => {
    if (joining) return;
    setJoining(true);
    setJoinError("");
    try {
      const name = label.trim().slice(0, 24);
      const basket = await joinGroupBasket(token, name || undefined);
      trackGroupBasketJoin(!accountCreatedFromGroupLink(token));
      clearGroupBasketToken();
      forgetMyGroupBaskets();
      history.replace(groupBasketPath(basket?.id || preview?.basketId));
    } catch (err) {
      setJoinError(err.message);
      setJoining(false);
      if (["BASKET_NOT_OPEN", "BASKET_FULL"].includes(err.code)) load();
    }
  };

  const signInHere = (authPath) => goToSignIn(history, groupJoinPath(token), authPath);

  const heroTitle =
    status === "ready" && preview
      ? `${preview.hostLabel} invited you to order together`
      : status === "invalid"
        ? "This link has ended"
        : "Order together";

  return (
    <Page>
      <Navbar bgColor="#2f474e" />
      <Hero>
        <HeroInner>
          <Eyebrow $onDark>Order together</Eyebrow>
          <HeroTitle>{heroTitle}</HeroTitle>
          {status === "ready" && preview && (
            <>
              <HeroLine>
                <GroupsOutlinedIcon aria-hidden="true" />
                <span>
                  {preview.vendor?.name}. {preview.memberCount} of {preview.maxMembers} joined.
                </span>
              </HeroLine>
              {preview.joinable && (
                <div>
                  <GroupBasketCountdown
                    deadline={{ kind: "CUTOFF", at: preview.cutoffAt }}
                    serverNow={preview.serverNow}
                    receivedAt={receivedAt}
                  />
                </div>
              )}
            </>
          )}
        </HeroInner>
      </Hero>

      <Shell>
        <Card>
          {status === "loading" && <Muted>Opening the basket.</Muted>}

          {status === "invalid" && (
            <Stack>
              <Muted>The basket may have ended, or the link is not complete. Ask for a new link.</Muted>
              <Actions>
                <QuietButton as={Link} to="/restaurants">
                  Browse restaurants
                </QuietButton>
              </Actions>
            </Stack>
          )}

          {status === "failed" && (
            <Stack>
              <Muted>{error?.message || "The basket could not be opened. Check your connection."}</Muted>
              <Actions>
                <PrimaryButton type="button" onClick={load}>
                  Try again
                </PrimaryButton>
              </Actions>
            </Stack>
          )}

          {status === "ready" && preview && (
            <Stack $gap={14}>
              <div>
                <CardTitle>One order from {preview.vendor?.name}</CardTitle>
                <Muted>
                  Add your own items. One rider brings everything to {preview.hostLabel}&rsquo;s drop-off.{" "}
                  {preview.mode === MODE.HOST_PAYS_ALL
                    ? `${preview.hostLabel} pays for the order.`
                    : "Everyone pays their own share by mobile money or card."}
                </Muted>
              </div>

              {!preview.joinable ? (
                <>
                  <Notice $tone="wait">
                    {NOT_JOINABLE_TEXT[preview.notJoinableReason] || "This basket cannot take new members."}
                  </Notice>
                  <Actions>
                    <QuietButton as={Link} to="/restaurants">
                      Browse restaurants
                    </QuietButton>
                  </Actions>
                </>
              ) : signedIn ? (
                <>
                  <div>
                    <FieldLabel htmlFor="group-join-label">Your name in the basket</FieldLabel>
                    <TextInput
                      id="group-join-label"
                      value={label}
                      maxLength={24}
                      onChange={(e) => setLabel(e.target.value)}
                      placeholder="Your first name"
                    />
                  </div>
                  {joinError ? <Notice $tone="danger" role="alert">{joinError}</Notice> : null}
                  <Actions style={{ marginTop: 0 }}>
                    <PrimaryButton type="button" onClick={join} disabled={joining}>
                      {joining ? "Joining" : "Join the basket"}
                    </PrimaryButton>
                  </Actions>
                </>
              ) : (
                <>
                  <Muted>Sign in to add your items. It takes a minute.</Muted>
                  <Actions style={{ marginTop: 0 }}>
                    <PrimaryButton type="button" onClick={() => signInHere("/login")}>
                      Sign in to join
                    </PrimaryButton>
                    <QuietButton type="button" onClick={() => signInHere("/register")}>
                      Create an account
                    </QuietButton>
                  </Actions>
                </>
              )}
            </Stack>
          )}
        </Card>
      </Shell>
    </Page>
  );
};

export default GroupBasketJoinPage;
